import { Component, effect, inject, OnInit, Signal, viewChild } from '@angular/core';
import { TranslocoPipe } from '@ngneat/transloco';
import { Store } from '@ngxs/store';
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { UntilDestroy } from '@ngneat/until-destroy';
import { IonicModule, Platform } from '@ionic/angular';
import { NotyService } from '@service/noty.service';
import { LangService } from '@service/lang.service';
import { RoomService } from '@service/room.service';
import { RoomMode, RoomUser, SiteInfo, UserRole } from '../../transport.interface';
import { UserState } from '../../store/user/user-state.service';
import { Pages } from '@app/pages';
import { IonMenu } from '@ionic/angular/standalone';
import { UserCardComponent } from '../ui/user-card/user-card.component';
import { TariffInfoComponent } from '../tariff-info/tariff-info.component';
import { addIcons } from 'ionicons';
import {
  calendarClearOutline,
  chatbubbleOutline,
  chatbubblesOutline,
  closeOutline,
  documentsOutline,
  personAddOutline,
  personCircleOutline,
  qrCodeOutline,
  syncOutline
} from 'ionicons/icons';
import { DialogService } from '@service/dialog.service';
import { StorageService } from '@service/storage.service';
import { Router } from '@angular/router';
import { SiteInfoState } from '@shared/store/site/site-state.service';
import { environment } from '@env/environment';

@UntilDestroy()
@Component({
  selector: 'app-nav-menu',
  standalone: true,
  imports: [
    TranslocoPipe,
    NgTemplateOutlet,
    NgOptimizedImage,
    IonicModule,
    UserCardComponent,
    TariffInfoComponent
  ],
  templateUrl: './nav-menu.component.html',
  styleUrl: './nav-menu.component.scss'
})
export class NavMenuComponent implements OnInit {
  protected _roomService = inject(RoomService);
  protected mainMenu = viewChild<IonMenu>('mainMenu');

  private readonly _platform = inject(Platform);
  private readonly _router = inject(Router);
  private readonly _dialogService = inject(DialogService);
  private readonly _store = inject(Store);
  private readonly _notyService = inject(NotyService);
  private readonly _storageService = inject(StorageService);
  protected readonly siteInfo: Signal<SiteInfo> = this._store.selectSignal(SiteInfoState.getSiteInfo);

  protected me: Signal<RoomUser> = this._store.selectSignal(UserState.getUser);
  protected readonly UserRole = UserRole;
  protected logged = false;
  protected readonly Pages = Pages;
  protected pending = {};
  protected hideInstallButton = false;
  protected isMobile = this._platform.is('capacitor') && !this._platform.is('mobileweb');
  protected isWeb = this._platform.is('desktop') || this._platform.is('mobileweb');
  protected isIos = this._platform.is('ios') && this._platform.is('capacitor');

  constructor() {
    addIcons({ closeOutline, qrCodeOutline, chatbubbleOutline, chatbubblesOutline, personAddOutline, personCircleOutline, syncOutline, calendarClearOutline, documentsOutline });
    this._storageService.logged().then(logged => {
      this.logged = logged && this.me()?.role !== UserRole.TEMP_USER;
    });
    effect(() => {
      this.logged = !!this.me()?.id;
    });
  }

  ngOnInit(): void {
    this.checkPWAInstallation();
  }

  buyMinutes(): void {
    this.mainMenu()?.close(true);
    if (!this.logged) {
      this._router.navigate([ '/', Pages.LOGIN ]);
      return;
    }
    this._dialogService.buyPackage(this.me().tariff);
  }

  navigate(page?: Pages): void {
    this._router.navigate([ '/', page || '' ]);
    this.mainMenu()?.close(true);
  }

  openRoomsHistory(): void {
    this.mainMenu()?.close(true);
    if (!this.logged) {
      this._router.navigate([ '/', Pages.LOGIN ]);
      return;
    }
    this._dialogService.roomsHistory();
  }

  openDocuments(): void {
    this.mainMenu()?.close(true);
    if (!this.logged) {
      this._router.navigate([ '/', Pages.LOGIN ]);
      return;
    }
    this._dialogService.myDocuments();
  }

  openProfile(): void {
    if (!this.logged) {
      this._router.navigate([ '/', Pages.LOGIN ]);
      return;
    }
    this.mainMenu()?.close(true);
    this._dialogService.profile().then();
  }

  joinRoomByQR(): void {
    this.mainMenu()?.close(true);
    if (this._platform.is('desktop')) {
      this._dialogService.joinRoom();
      return;
    }
    this._dialogService.qrCodeScanner().subscribe(code => {
      if (code) {
        this._router.navigate([ '/', Pages.JOIN_ROOM, code ]);
      }
    });
  }

  startChat(mode: RoomMode): void {
    if (this.me().role === UserRole.TEMP_USER || !this.me()?.id) {
      this._notyService.success('message.register');
      return;
    }
    // this._audioService.initAudioForIos();
    this._storageService.clearRoomId();
    this.mainMenu()?.close(true);
    let m = mode === RoomMode.SINGLE ? 'single' : 'conference';
    this._router.navigate([ '/', Pages.CHAT ], { queryParams: { mode: m } });
  }

  openPage(page: Pages): void {
    this.mainMenu()?.close(true);
    // this._dialogService.closeAll();
    this._router.navigate([ '/', page ]);
  }

  openHelp(): void {
    this.mainMenu()?.close(true);
    this._dialogService.help();
  }

  openTerms(): void {
    this.mainMenu()?.close(true);
    this._dialogService.terms();
  }

  openPrivacy(): void {
    this.mainMenu()?.close(true);
    this._dialogService.privacy();
  }

  installApp(): void {
    this.mainMenu()?.close(true);
    // this._dialogService.closeAll();
    this._storageService.delete('hide_pwa');
    // this._dialogService.installAppEvent(true);
  }

  async checkPWAInstallation() {
    if ('getInstalledRelatedApps' in window.navigator) {
      // @ts-ignore
      const apps = await navigator.getInstalledRelatedApps();
      apps.forEach((app: any) => {
        if (app.url.indexOf('voice') >= 0) {
          this.hideInstallButton = true;
        }
      });
    }
    // @ts-ignore
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      this.hideInstallButton = true;
    }
  }

  protected readonly RoomMode = RoomMode;

  completeRegistration(): void {
    this.mainMenu()?.close(true);
    this._dialogService.completeRegistration();
  }

  protected readonly environment = environment;
}
